/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { html, css, ref, createRef } from 'lit-all'
import { getViewport, htmlStringToNode } from 'lib-utils'
import styles from 'lib-styles'
import * as paths from 'lib-paths'
/* eslint-disable import/no-unresolved */

class CardCarousel extends BaseElement {
  static properties = {
    viewport: {
      type: String,
      state: true,
    },
    activeIndex: {
      type: Number,
      state: true,
    },
    showOriginRatio: {
      type: Boolean,
      attribute: 'show-origin-ratio',
    },
  }

  $carousel = createRef()

  get slidesPerPage() {
    switch (this.viewport) {
      case 'mobile-small':
        return 1.5
      case 'mobile':
        return 1.5
      case 'tablet-small':
        return 1.5
      case 'tablet':
        return 2.5
      case 'desktop-small':
        return 3.5
      case 'desktop':
        return 3.5
      default:
        return 3.5
    }
  }

  constructor() {
    super()
    this.cards = []
    this.activeIndex = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this.cards = JSON.parse(
      this.querySelector('[data-cards]')?.textContent || '[]'
    )
    this._handleResize()
    window.addEventListener('hh:resize-width', this._handleResize)
  }

  disconnectedCallback() {
    window.removeEventListener('hh:resize-width', this._handleResize)
  }

  _handleResize = () => {
    this.viewport = getViewport()
  }

  _handleNext = () => {
    this.$carousel.value.next()
  }

  _handlePrev = () => {
    this.$carousel.value.previous()
  }

  _handleCarouselSlideChange = (event) => {
    this.activeIndex = event.detail.index
  }

  render() {
    return html`
      <div class="carousel-container">
        <sl-carousel
          slides-per-move="1"
          mouse-dragging
          ${ref(this.$carousel)}
          slides-per-page=${this.slidesPerPage}
          @sl-slide-change=${this._handleCarouselSlideChange}
          class="
            ${this.cards.length < 4 ? 'center-content' : ''}
            ${this.showOriginRatio ? 'show-origin-ratio' : ''}
          "
        >
          ${this.cards.map(
            (card) => html`
              <sl-carousel-item role="group" aria-label="card">
                <a class="card" href=${card.link.length ? card.link : ''}>
                  <div>
                    <img
                      src=${card.image}
                      width="100%"
                      height="100%"
                      alt=${card.image.alt}
                    />
                  </div>
                  <div class="card-inner">
                    <p class="h5">${card.title}</p>
                    <p class="utility">${htmlStringToNode(card.description)}</p>
                  </div>
                </a>
              </sl-carousel-item>
            `
          )}
        </sl-carousel>

        <button
          class="navigation-button navigation-button--previous"
          ?hidden=${this.activeIndex === 0 || this.cards.length < 4}
          @click=${this._handlePrev}
        >
          ${paths.arrowWithStem}
        </button>

        <button
          class="navigation-button navigation-button--next"
          ?hidden=${this.activeIndex ===
            this.cards.length - Math.floor(this.slidesPerPage) ||
          this.cards.length < 4}
          @click=${this._handleNext}
        >
          ${paths.arrowWithStem}
        </button>
      </div>
    `
  }
  static styles = [
    styles.flex,
    styles.a11y,
    css`
      :host {
        display: block;
        max-width: 1440px;
        margin-left: 16px;
        margin-top: 16px;
      }

      .navigation-button {
        border-radius: 4px;
        background: var(--Reverse, #fff);
        border: 1px solid var(--Dark-Green, #35332b);
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        display: none;
        position: absolute;
        cursor: pointer;
        top: 50%;
        transform: translateY(-50%);
      }

      .navigation-button--previous svg {
        transform: scaleX(-1);
      }

      .navigation-button--previous {
        left: 52px;
      }

      .navigation-button--next {
        right: 52px;
      }

      .navigation-button svg {
        width: 24px;
        height: 18px;
      }

      .carousel-container {
        position: relative;
      }

      sl-carousel {
        --aspect-ratio: 1 / 1.22;
        --slide-gap: 16px;
      }

      .show-origin-ratio {
        --aspect-ratio: auto;
      }

      /* Shoelace's carousel has a limitation in that it expects an aspect ratio
         rather than being able to adapt to slide content height. As a temporary
         work around we are controlling this manually to look OK across breakpoints. */
      @media screen and (min-width: 310px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.6;
        }
      }
      @media screen and (min-width: 320px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.6;
        }
      }
      @media screen and (min-width: 350px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.4;
        }
      }
      @media screen and (min-width: 375px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.3;
        }
      }
      @media screen and (min-width: 400px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.2;
        }
      }
      @media screen and (min-width: 460px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.1;
        }
      }
      @media screen and (min-width: 520px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1;
        }
      }
      @media screen and (min-width: 600px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 0.9;
        }
      }
      @media screen and (min-width: 769px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.2;
        }
      }
      @media screen and (min-width: 800px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.1;
        }
      }
      @media screen and (min-width: 900px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1;
        }
      }
      @media screen and (min-width: 1025px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.2;
        }
      }
      @media screen and (min-width: 1150px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.1;
        }
      }
      @media screen and (min-width: 1200px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1.1;
        }
      }
      @media screen and (min-width: 1250px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 1;
        }
      }
      @media screen and (min-width: 1300px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 0.97;
        }
      }
      @media screen and (min-width: 1350px) {
        sl-carousel:not(.show-origin-ratio) {
          --aspect-ratio: 1 / 0.95;
        }
      }

      sl-carousel::part(scroll-container) {
        padding-right: 16px;
      }

      sl-carousel::part(base) {
        gap: 0;
      }

      sl-carousel-item {
        height: 100%;
      }

      sl-carousel-item:hover {
        box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.05);
      }

      .card {
        height: 100%;
        width: 100%;
        text-align: center;
        text-decoration: none;
        display: block;
        background: #fff;
      }

      .card img {
        display: flex;
      }

      .card p.h5 {
        color: var(--Dark-Green, #35332b);
        font-family: 'PP Right Grotesk';
        font-size: 24px;
        margin: 0 0 5px 0;
        text-wrap: balance;
        line-height: normal;
        font-weight: 500;
      }

      .card p.utility {
        color: var(--Dark-Green, #35332b);
        font-family: 'Inter';
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        margin: 0 auto;
        max-width: 260px;
      }

      .card-inner {
        padding: 10px 20px;
      }

      @media screen and (min-width: 768px) {
        :host {
          margin-top: 30px;
          padding: 0px 33px;
          margin-right: auto;
          margin-left: auto;
        }

        sl-carousel {
          --slide-gap: 20px;
        }

        sl-carousel::part(scroll-container) {
          padding-right: 0;
        }

        sl-carousel.center-content::part(scroll-container) {
          justify-content: center;
        }

        .navigation-button {
          display: flex;
        }
        sl-card p.h5 {
          line-height: unset;
        }
        .card-inner {
          padding: 20px;
        }
      }
    `,
  ]
}
customElements.define('card-carousel', CardCarousel)
